import { ContentType, IGroupPictureDto } from '@kcftech/prometheus-client';
import { MutationFunction, UseMutationOptions } from '@tanstack/react-query';
import { FileLibraryGroup } from 'components/asset/asset_file_library/types';
import { useNotificationContext } from 'contexts/NotificationContext';
import { usePermissions } from 'contexts/PermissionsContext';
import { usePopup } from 'custom_hooks/usePopup';
import { useState } from 'react';

import { PathToIdMapping } from '../hooks/useFolderIdsInPath';

export type FileIsh = {
    Id: string;
    /** display name */
    Name: string;
    /** storage key (files only) */
    StorageFileName?: string;
    /** thumbnail storage key (files only) */
    ThumbnailStorageFileName?: string;
    /** entry type - Folder/Picture/File/Video */
    EntryType: ContentType;
    /** Media type (e.g. image/jpeg, video/mp4) -- should be empty only for folders */
    MediaType?: string;
    ParentFolderId?: string;
    Description?: string;
    // TODO @jp: I may want to tear this out and load only later, but will leave for now.
    Groups?: IGroupPictureDto[];

    /**
     * note that group information must be explicitly included
     * for this to be present, which only some views do
     */
    groupContext?: {
        IsPrimaryImage?: boolean;
        Role?: string;
    };
};

export type ActiveAction = 'delete' | 'unlink-group' | 'edit-folder' | 'move';

export type FileActionsBaseProps = Readonly<{
    rowData: FileIsh;
    onChange: () => void;
    group?: FileLibraryGroup;
    enableModal: boolean;
    locationId?: string;
    corporationId?: string;
    contextGroupId?: string;
    foldersHierarchy: PathToIdMapping[];
}>;

type ActionMutationOpts<TData, TVariables> = {
    mutationFn: MutationFunction<TData, TVariables>;
    successMessage?: string;
    errorMessage: string;
    onSuccess?: (data: TData) => void;
    onError?: () => void;
};

export function useFileActions(props: FileActionsBaseProps) {
    const { rowData, group, contextGroupId } = props;
    const [activeAction, setActiveAction] = useState<ActiveAction>();
    const { showToast } = useNotificationContext();
    const { canEditFileLibrary, canDeleteFromFileLibrary } = usePermissions();
    const popup = usePopup();

    const fileType = rowData.EntryType;
    const hasGroupActions = !!(group && (!contextGroupId || group.Id === contextGroupId));

    function openAction(action: ActiveAction) {
        popup.close();
        setActiveAction(action);
    }

    function closeAction() {
        setActiveAction(undefined);
    }

    function actionMutationOptions<TData = void, TVariables = void>(
        opts: ActionMutationOpts<TData, TVariables>,
    ): UseMutationOptions<TData, Error, TVariables> {
        return {
            mutationFn: opts.mutationFn,
            onSuccess: (data) => {
                popup.close();
                if (opts.successMessage) {
                    showToast({ type: 'success', content: opts.successMessage });
                }
                opts.onSuccess?.(data);
            },
            onError: () => {
                showToast({ type: 'error', content: opts.errorMessage });
                opts.onError?.();
            },
        };
    }

    return {
        ...props,
        activeAction,
        openAction,
        closeAction,
        canEditFileLibrary,
        canDeleteFromFileLibrary,
        popup,
        fileType,
        hasGroupActions,
        actionMutationOptions,
    };
}

export type FileActionsBase = ReturnType<typeof useFileActions>;
